// src/api/acceso.js
// ⭐ TORNIQUETE: usa el AGENTE LOCAL vía Tailscale Funnel / red local
// Las validaciones van al backend web
import { apiFetch } from './index.js';

// URL por defecto del Agente Local en la laptop del Gym (Tailscale Funnel)
const DEFAULT_AGENTE_URL = 'https://desktop-qagollm.tailb8d19f.ts.net';

export const getAgenteUrl = () => {
  const custom = localStorage.getItem('agente_url');
  return (custom && custom.trim() !== '') ? custom.trim().replace(/\/+$/, '') : DEFAULT_AGENTE_URL;
};

export const setAgenteUrl = (url) => {
  if (url && url.trim() !== '') {
    localStorage.setItem('agente_url', url.trim().replace(/\/+$/, ''));
  } else {
    localStorage.removeItem('agente_url');
  }
};

// =====================================================
// TORNIQUETE (vía agente local en la laptop del Gym)
// =====================================================

/**
 * Verifica el estado del torniquete (llama al agente local).
 */
export const estadoTorniquete = async () => {
  const agenteUrl = getAgenteUrl();
  try {
    const response = await fetch(`${agenteUrl}/estado`, {
      method: 'GET',
      signal: AbortSignal.timeout(10000)
    });
    const data = await response.json();
    return {
      success: true,
      data: {
        conectado: data.conectado ?? false,
        puerto: data.puerto ?? 'COM4',
        mensaje: data.mensaje ?? ''
      },
      ...data
    };
  } catch (error) {
    console.warn('⚠️ No se pudo consultar estado del torniquete en agente local:', error.message);
    // Intentar fallback al backend
    try {
      const fb = await apiFetch('/acceso/estado-hardware');
      return {
        success: true,
        data: {
          conectado: fb.conectado ?? false,
          puerto: fb.puerto ?? 'COM4',
          mensaje: fb.mensaje ?? ''
        },
        ...fb
      };
    } catch {
      return {
        success: false,
        error: error.message,
        data: { conectado: false, puerto: 'COM4' }
      };
    }
  }
};

/**
 * Abre el torniquete (llama al agente local vía puerto COM).
 */
export const reproducirAudioAgente = async (audioKey = 'bienvenida') => {
  const agenteUrl = getAgenteUrl();
  try {
    const res = await fetch(`${agenteUrl}/reproducir-audio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioKey }),
      signal: AbortSignal.timeout(4000)
    });
    return await res.json();
  } catch (e) {
    console.warn('⚠️ No se pudo reproducir audio en agente local:', e.message);
    return { success: false, error: e.message };
  }
};

/**
 * Abre el torniquete (llama al agente local vía puerto COM con audio sincronizado).
 */
export const abrirTorniqueteManual = async (audioKey = 'bienvenida') => {
  const agenteUrl = getAgenteUrl();
  try {
    const response = await fetch(`${agenteUrl}/abrir-torniquete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioKey }),
      signal: AbortSignal.timeout(15000)
    });
    const data = await response.json();
    return {
      success: data.success !== false,
      mensaje: data.mensaje ?? 'Torniquete abierto por agente local',
      ...data
    };
  } catch (error) {
    console.warn('⚠️ Falló agente local para abrir torniquete, intentando backend:', error.message);
    // Fallback a backend en caso de conexión directa
    try {
      const fb = await apiFetch('/acceso/abrir-manual', {
        method: 'POST',
        body: JSON.stringify({ metodo: 'manual' })
      });
      return {
        success: true,
        mensaje: fb.mensaje ?? 'Torniquete abierto por backend',
        ...fb
      };
    } catch (fbError) {
      console.error('❌ Error abriendo torniquete:', fbError);
      return { success: false, error: error.message };
    }
  }
};

// =====================================================
// VALIDACIÓN DE ACCESO (vía backend web)
// =====================================================

/**
 * Valida el acceso de un socio por CÉDULA.
 * Envía la cédula al backend web, emite SignalR y valida membresía.
 */
export const validarAccesoPorCedula = async (cedula) => {
  try {
    const response = await apiFetch('/Huella/identificar-cedula', {
      method: 'POST',
      body: JSON.stringify({ cedula })
    });
    return { success: true, ...response };
  } catch (error) {
    console.warn('⚠️ Error en /Huella/identificar-cedula, intentando fallback /Membresia/acceso/cedula:', error.message);
    try {
      const fb = await apiFetch('/Membresia/acceso/cedula', {
        method: 'POST',
        body: JSON.stringify({ cedula })
      });
      return { success: true, ...fb };
    } catch (fbError) {
      console.error('❌ Error validando acceso por cédula:', fbError);
      return { success: false, permitido: false, mensaje: fbError.message || error.message };
    }
  }
};

/**
 * Valida el acceso de un cliente/membresía de forma genérica.
 */
export const validarAcceso = async (datos) => {
  try {
    const response = await apiFetch('/acceso/validar', {
      method: 'POST',
      body: JSON.stringify(datos)
    });
    return { success: true, ...response };
  } catch (error) {
    console.error('❌ Error validando acceso:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cambia el puerto COM del agente local o del backend.
 */
export const cambiarPuertoTorniquete = async (puerto) => {
  const agenteUrl = getAgenteUrl();
  try {
    const response = await fetch(`${agenteUrl}/cambiar-puerto`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ puerto })
    });
    const data = await response.json();
    return { success: data.success !== false, mensaje: data.mensaje };
  } catch {
    try {
      const fb = await apiFetch('/acceso/cambiar-puerto', {
        method: 'POST',
        body: JSON.stringify({ puerto })
      });
      return { success: fb.exito ?? true, mensaje: fb.mensaje };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

export default {
  getAgenteUrl,
  setAgenteUrl,
  estadoTorniquete,
  abrirTorniqueteManual,
  validarAccesoPorCedula,
  validarAcceso,
  cambiarPuertoTorniquete
};
